import { useEffect, useMemo, useState } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetDatesQuery, useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectAnalysisStartDate,
  selectMatrixVisualize,
  selectODUIMode,
  selectShowViewAllModal,
  setAnalysisEndDate,
  setAnalysisHasVolume,
  setAnalysisName,
  setAnalysisSegments,
  setAnalysisStartDate,
  setDayTypeSetting,
  setIncludeMtr,
  setShowViewAllModal,
  setTimePeriod,
  useGetAnalysesQuery,
} from 'state/originDestinationSlices';
import {
  selectCurrentAnalysisId,
  selectMaximize,
  selectProject,
  setMaximize,
} from 'state/workflowSlice';

import { MAXIMIZE_MAP, ODUIMode } from 'appConstants';
import NoAccessPopup from 'features/common/NoAccessPopup';
import StlViewAllModal from 'features/common/StlViewAllModal/StlViewAllModal';
import { parseMonthYear } from 'features/common/datetimeUtils';
import { capitalizeFirstLetter } from 'features/common/utils';
import { MapContextProvider } from 'features/map/MapContext';
import { withSegmentTooltip } from 'features/map/hooks/withSegmentTooltip';
import LoadingSpinner from 'features/spinner/Spinner';
import { formatDateToMonthYear } from 'features/user_admin/SpecialDateRequest/utils';
import { workflowStyles } from 'features/workspace/workspaceUtils';

import { ReliesOn } from '../workflow_common/NoDataLoadedWarning';
import { WorkflowInfo } from '../workflow_common/workflowLookup';
import ODCreateModal from './ODCreateModal';
import ODList from './ODList';
import CollapseODMatrix from './ODMatrix/CollapseODMatrix';
import ODSidebar from './ODSidebar/ODSidebar';
import { useODUIMode } from './hooks/useODUIMode';
import { ODMap } from './map/ODMap';

import styles from '../workspace/Workspace.module.css';

export const workflowOriginDestination: WorkflowInfo = {
  dataAvailableReliesOn: [ReliesOn.nrtDay, ReliesOn.typicalMonth],
  switchToState: (fromWorkflow, state) => {},
};
const itemName = 'analysis';
const MapWithTooltip = withSegmentTooltip(ODMap);

export function WorkflowOriginDestination() {
  const dispatch = useAppDispatch();

  const [layer, setLayer] = useState(null);
  const uiMode = useAppSelector(selectODUIMode);
  const currentAnalysisId = useAppSelector(selectCurrentAnalysisId);
  const viewAllModal = useAppSelector(selectShowViewAllModal);
  const maximize = useAppSelector(selectMaximize);
  const project = useAppSelector(selectProject);
  const visualize = useAppSelector(selectMatrixVisualize);
  const currentStartDate = useAppSelector(selectAnalysisStartDate);

  const { setCreateMode, setInitialMode } = useODUIMode();
  const { map_style } = workflowStyles(maximize);
  const { currentData: datesInfo } = useGetDatesQuery(project);
  const memoizedLayer = useMemo(() => ({ layer, setLayer }), [layer]);
  const {
    currentData: analyses,
    isLoading,
    isFetching,
  } = useGetAnalysesQuery(project, {
    skip: !project,
  });
  const analysis = analyses?.find((el) => el.analysis_id === currentAnalysisId);
  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project);

  const inCreateMode = uiMode === ODUIMode.Create;

  useEffect(() => {
    if (!datesInfo || !typicalMonthsInfo) {
      setLayer(undefined);
      return;
    }

    if (!currentStartDate) return;

    const currentMonth = formatDateToMonthYear(currentStartDate);

    const newLayer =
      datesInfo.find(
        (info) => formatDateToMonthYear(info.date) === currentMonth,
      )?.layer ??
      typicalMonthsInfo.find(
        (info) =>
          formatDateToMonthYear(parseMonthYear(info.typicalMonth)) ===
          currentMonth,
      )?.layer;
    if (newLayer !== layer) setLayer(newLayer);
  }, [datesInfo, currentStartDate, layer, setLayer, typicalMonthsInfo]);

  /** prevent the case when a map's display: none on initial render (maximize is set to MAXIMIZE_DATA) */
  useEffect(() => {
    if (!visualize) dispatch(setMaximize(MAXIMIZE_MAP));
  }, [visualize]);

  // transfer initial data into redux
  useEffect(() => {
    if (analysis) {
      const segments = analysis.tt_seg_ids.map((id, index) => ({
        id,
        name: analysis.segment_display_names[index],
      }));
      dispatch(setAnalysisSegments(segments));
      dispatch(setAnalysisStartDate(analysis.start_date));
      dispatch(setAnalysisEndDate(analysis.end_date));
      dispatch(setDayTypeSetting(analysis.day_type_setting));
      dispatch(setTimePeriod(analysis.time_period));
      dispatch(setIncludeMtr(analysis.include_mtr));
      dispatch(setAnalysisName(analysis.name));
      dispatch(setAnalysisHasVolume(analysis.has_volume));
    }
  }, [analysis]);

  const analysesResolved = !isFetching && !isLoading && analyses !== undefined;
  const notFoundError =
    analysesResolved && currentAnalysisId !== undefined && !analysis;
  const showCreateModal =
    analysesResolved &&
    !notFoundError &&
    analyses.length === 0 &&
    !inCreateMode;
  const showViewAllModal =
    analysesResolved &&
    !notFoundError &&
    !showCreateModal &&
    (viewAllModal || (!inCreateMode && currentAnalysisId === undefined));

  return (
    <LayerContext.Provider value={memoizedLayer}>
      <MapContextProvider>
        <div
          className={styles.workspace_container}
          id={styles.workspace_container_od}
        >
          <div className={styles.workspace_content}>
            <div className={map_style}>
              <MapWithTooltip />
            </div>
            {visualize && <CollapseODMatrix />}
          </div>

          {(currentAnalysisId || inCreateMode) && <ODSidebar />}
          {isLoading ? (
            <div className={styles.loader_wrapper}>
              <LoadingSpinner large />
            </div>
          ) : (
            <>
              {notFoundError && (
                <NoAccessPopup
                  open={notFoundError}
                  itemName={itemName}
                  onClose={setInitialMode}
                />
              )}
              {showCreateModal && <ODCreateModal />}
              {showViewAllModal && (
                <StlViewAllModal
                  itemName={capitalizeFirstLetter(itemName)}
                  pluralItemName="Analyses"
                  title="Origin Destination Analyses"
                  handleCreate={setCreateMode}
                  listComponent={ODList}
                  nameAccessor="name"
                  showCloseBtn={viewAllModal}
                  onClose={() => dispatch(setShowViewAllModal(false))}
                />
              )}
            </>
          )}
        </div>
      </MapContextProvider>
    </LayerContext.Provider>
  );
}
